<script lang="ts" setup>
import type { DropDownItem } from '@/components/common/DropDown.vue';
import { financialConverter } from '@/converters/financials/financials-converter';
import { notificationService } from '@/services/notification/notification-service';
import { FinancialConverterError } from '@/utils/errors/converters/financial-converter-errors';
import { VatType } from 'cesg-shared/dist/models';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface SupplierDetails {
  organisationNumber: string;
  isActive: boolean;
  name: string;
  phone: string;
  email: string;
  streetName: string;
  buildingNumber: string;
  zipCode: string;
  city: string;
  country: string;
  countryCode: string;
  bankGiro: string;
  postGiro: string;
  bic: string;
  iban: string;
  accountType: string;
  bankName: string;
  clearingNumber: string;
  accountNumber: string;
  defaultAccount: string;
  vatNumber: string;
  vatType: string;
  currency: string;
}

export type NewSupplierSaveEmit = (data: SupplierDetails) => void;

interface Emits {
  (e: 'close'): void;
  (
    e: 'save',
    ...props: Parameters<NewSupplierSaveEmit>
  ): ReturnType<NewSupplierSaveEmit>;
}

interface Props {
  supplierDetails?: Partial<SupplierDetails>;
  isOpen?: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();
const emit = defineEmits<Emits>();

// Basic Info

const formData = ref<SupplierDetails>({
  organisationNumber: '',
  isActive: true,
  name: '',
  phone: '',
  email: '',
  streetName: '',
  buildingNumber: '',
  zipCode: '',
  city: '',
  country: '',
  countryCode: 'SE',
  bankGiro: '',
  postGiro: '',
  bic: '',
  iban: '',
  accountType: '',
  bankName: '',
  clearingNumber: '',
  accountNumber: '',
  defaultAccount: '',
  vatNumber: '',
  vatType: '',
  currency: 'SEK',
});

const isLoading = ref(false);
const isVatNumberManuallyEdited = ref(false);

const toVatNumberFromOrganisationNumber = (orgNumber: string) => {
  const compactOrgNumber = orgNumber
    .replace(/[^\p{L}\p{N}]/gu, '')
    .toUpperCase();
  return compactOrgNumber ? `SE${compactOrgNumber}01` : '';
};

const syncVatNumberFromOrganisationNumber = () => {
  if (isVatNumberManuallyEdited.value) {
    return;
  }

  formData.value.vatNumber = toVatNumberFromOrganisationNumber(
    formData.value.organisationNumber,
  );
};

watch(
  () => formData.value.organisationNumber,
  () => {
    syncVatNumberFromOrganisationNumber();
  },
);

const vatTypeOptions = computed<DropDownItem[]>(() =>
  Object.values(VatType).map((value) => ({
    value,
    text: t(`adminCreateInvoice.bottomRows.vatTypes.${value}`),
  })),
);

// Watch for prop changes and update form fields
watch(
  () => props.isOpen,
  (newValue) => {
    const details = props.supplierDetails || {};
    if (newValue) {
      // Initialize form with prop values when panel opens
      isLoading.value = false;
      isVatNumberManuallyEdited.value = false;
      formData.value.organisationNumber = details.organisationNumber || '';
      formData.value.isActive =
        details.isActive !== undefined ? details.isActive : true;
      formData.value.name = details.name || '';
      formData.value.phone = details.phone || '';
      formData.value.email = details.email || '';
      formData.value.streetName = details.streetName || '';
      formData.value.buildingNumber = details.buildingNumber || '';
      formData.value.zipCode = details.zipCode || '';
      formData.value.city = details.city || '';
      formData.value.country = details.country || '';
      formData.value.countryCode = details.countryCode || 'SE';
      formData.value.bankGiro = details.bankGiro || '';
      formData.value.postGiro = details.postGiro || '';
      formData.value.bic = details.bic || '';
      formData.value.iban = details.iban || '';
      formData.value.accountType = details.accountType || '';
      formData.value.bankName = details.bankName || '';
      formData.value.clearingNumber = details.clearingNumber || '';
      formData.value.accountNumber = details.accountNumber || '';
      formData.value.defaultAccount = details.defaultAccount || '';
      formData.value.vatNumber = details.vatNumber || '';
      formData.value.vatType = details.vatType || '';
      formData.value.currency = details.currency || 'SEK';

      isVatNumberManuallyEdited.value = Boolean(details.vatNumber?.trim());
      syncVatNumberFromOrganisationNumber();
    } else {
      // Reset form when panel closes
      resetForm();
    }
  },
);

const handleSave = () => {
  // Validate required fields
  if (
    !formData.value.organisationNumber.trim() ||
    !formData.value.name.trim() ||
    !formData.value.streetName.trim() ||
    !formData.value.buildingNumber.trim() ||
    !formData.value.zipCode.trim() ||
    !formData.value.city.trim() ||
    !formData.value.vatNumber.trim() ||
    (!formData.value.bankGiro.trim() &&
      !formData.value.postGiro.trim() &&
      (!formData.value.iban.trim() || !formData.value.bic.trim()))
  ) {
    return;
  }

  try {
    financialConverter.toStrict({
      ...formData.value,
    });
  } catch (error) {
    if (error instanceof FinancialConverterError) {
      const key = `financialConverter.errors.${error.type}`;
      notificationService.open({
        type: 'danger',
        text: `${t(`${key}.message`)} (${t(`${key}.format`)})`,
      });
    }
    return;
  }

  isLoading.value = true;

  emit('save', {
    organisationNumber: formData.value.organisationNumber.trim(),
    isActive: formData.value.isActive,
    name: formData.value.name.trim(),
    phone: formData.value.phone.trim(),
    email: formData.value.email.trim(),
    streetName: formData.value.streetName.trim(),
    buildingNumber: formData.value.buildingNumber.trim(),
    zipCode: formData.value.zipCode.trim(),
    city: formData.value.city.trim(),
    country: formData.value.country.trim(),
    countryCode: formData.value.countryCode.trim(),
    bankGiro: formData.value.bankGiro.trim(),
    postGiro: formData.value.postGiro.trim(),
    bic: formData.value.bic.trim(),
    iban: formData.value.iban.trim(),
    accountType: formData.value.accountType.trim(),
    bankName: formData.value.bankName.trim(),
    clearingNumber: formData.value.clearingNumber.trim(),
    accountNumber: formData.value.accountNumber.trim(),
    defaultAccount: formData.value.defaultAccount.trim(),
    vatNumber: formData.value.vatNumber.trim(),
    vatType: formData.value.vatType.trim(),
    currency: formData.value.currency.trim(),
  });
};

const resetForm = () => {
  formData.value.organisationNumber = '';
  formData.value.isActive = true;
  formData.value.name = '';
  formData.value.phone = '';
  formData.value.email = '';
  formData.value.streetName = '';
  formData.value.buildingNumber = '';
  formData.value.zipCode = '';
  formData.value.city = '';
  formData.value.country = '';
  formData.value.countryCode = 'SE';
  formData.value.bankGiro = '';
  formData.value.postGiro = '';
  formData.value.bic = '';
  formData.value.iban = '';
  formData.value.accountType = '';
  formData.value.bankName = '';
  formData.value.clearingNumber = '';
  formData.value.accountNumber = '';
  formData.value.defaultAccount = '';
  formData.value.vatNumber = '';
  formData.value.vatType = '';
  formData.value.currency = 'SEK';
  isVatNumberManuallyEdited.value = false;
  isLoading.value = false;
};

const handleClose = () => {
  resetForm();
  emit('close');
};
</script>

<template>
  <Transition name="slide">
    <div v-if="isOpen" class="slide-panel-container">
      <div class="slide-panel">
        <header class="panel-header">
          <p class="panel-title">
            {{ t('adminSupplierRegister.addSupplier') }}
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="handleClose"
            type="button"
          ></button>
        </header>

        <section class="panel-body">
          <!-- Basic Information Section -->
          <div class="section-title">
            {{ t('adminSupplier.detailsBasicInfo.basicInfo') }}
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="organisationNumber"
                  >{{
                    t('adminSupplier.detailsBasicInfo.organisationNumber')
                  }}
                  *</label
                >
                <div class="control">
                  <input
                    id="organisationNumber"
                    v-model="formData.organisationNumber"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsBasicInfo.organisationNumber')
                    "
                    required
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="isActive">{{
                  t('adminSupplier.detailsBasicInfo.isActive')
                }}</label>
                <div class="control">
                  <label class="checkbox">
                    <input type="checkbox" v-model="formData.isActive" />
                    <span class="checkbox-label">
                      {{
                        formData.isActive
                          ? t('adminSupplier.detailsBasicInfo.isActiveYes')
                          : t('adminSupplier.detailsBasicInfo.isActiveNo')
                      }}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label" for="name"
              >{{ t('adminSupplier.detailsBasicInfo.supplierName') }} *</label
            >
            <div class="control">
              <input
                id="name"
                v-model="formData.name"
                class="input"
                type="text"
                :placeholder="t('adminSupplier.detailsBasicInfo.supplierName')"
                required
              />
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="phone">{{
                  t('adminSupplier.detailsBasicInfo.phone')
                }}</label>
                <div class="control">
                  <input
                    id="phone"
                    v-model="formData.phone"
                    class="input"
                    type="tel"
                    :placeholder="t('adminSupplier.detailsBasicInfo.phone')"
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="email">{{
                  t('adminSupplier.detailsBasicInfo.email')
                }}</label>
                <div class="control">
                  <input
                    id="email"
                    v-model="formData.email"
                    class="input"
                    type="email"
                    :placeholder="t('adminSupplier.detailsBasicInfo.email')"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="streetName"
                  >{{ t('adminSupplier.detailsBasicInfo.streetName') }} *</label
                >
                <div class="control">
                  <input
                    id="streetName"
                    v-model="formData.streetName"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsBasicInfo.streetName')
                    "
                    required
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="buildingNumber"
                  >{{
                    t('adminSupplier.detailsBasicInfo.buildingNumber')
                  }}
                  *</label
                >
                <div class="control">
                  <input
                    id="buildingNumber"
                    v-model="formData.buildingNumber"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsBasicInfo.buildingNumber')
                    "
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="zipCode"
                  >{{ t('adminSupplier.detailsBasicInfo.zipCode') }} *</label
                >
                <div class="control">
                  <input
                    id="zipCode"
                    v-model="formData.zipCode"
                    class="input"
                    type="text"
                    :placeholder="t('adminSupplier.detailsBasicInfo.zipCode')"
                    required
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="city"
                  >{{ t('adminSupplier.detailsBasicInfo.city') }} *</label
                >
                <div class="control">
                  <input
                    id="city"
                    v-model="formData.city"
                    class="input"
                    type="text"
                    :placeholder="t('adminSupplier.detailsBasicInfo.city')"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="country">{{
                  t('adminSupplier.detailsBasicInfo.country')
                }}</label>
                <div class="control">
                  <input
                    id="country"
                    v-model="formData.country"
                    class="input"
                    type="text"
                    :placeholder="t('adminSupplier.detailsBasicInfo.country')"
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="countryCode">{{
                  t('adminSupplier.detailsBasicInfo.countryCode')
                }}</label>
                <div class="control">
                  <input
                    id="countryCode"
                    v-model="formData.countryCode"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsBasicInfo.countryCode')
                    "
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Financials Section -->
          <div class="section-title">
            {{ t('adminSupplier.detailsFinancials.financials') }}
          </div>
          <p class="helper-text">
            * {{ t('adminSupplier.detailsFinancials.bgPgRequirement') }}
          </p>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="bankGiro">{{
                  t('adminSupplier.detailsFinancials.bankGiro')
                }}</label>
                <div class="control">
                  <input
                    id="bankGiro"
                    v-model="formData.bankGiro"
                    class="input"
                    type="text"
                    :placeholder="t('adminSupplier.detailsFinancials.bankGiro')"
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="postGiro">{{
                  t('adminSupplier.detailsFinancials.plusGiro')
                }}</label>
                <div class="control">
                  <input
                    id="postGiro"
                    v-model="formData.postGiro"
                    class="input"
                    type="text"
                    :placeholder="t('adminSupplier.detailsFinancials.plusGiro')"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="bic">{{
                  t('adminSupplier.detailsFinancials.bic')
                }}</label>
                <div class="control">
                  <input
                    id="bic"
                    v-model="formData.bic"
                    class="input"
                    type="text"
                    :placeholder="t('adminSupplier.detailsFinancials.bic')"
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="iban">{{
                  t('adminSupplier.detailsFinancials.iban')
                }}</label>
                <div class="control">
                  <input
                    id="iban"
                    v-model="formData.iban"
                    class="input"
                    type="text"
                    :placeholder="t('adminSupplier.detailsFinancials.iban')"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="accountType">{{
                  t('adminSupplier.detailsFinancials.accountType')
                }}</label>
                <div class="control">
                  <input
                    id="accountType"
                    v-model="formData.accountType"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsFinancials.accountType')
                    "
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="bankName">{{
                  t('adminSupplier.detailsFinancials.bankName')
                }}</label>
                <div class="control">
                  <input
                    id="bankName"
                    v-model="formData.bankName"
                    class="input"
                    type="text"
                    :placeholder="t('adminSupplier.detailsFinancials.bankName')"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="clearingNumber">{{
                  t('adminSupplier.detailsFinancials.clearingNumber')
                }}</label>
                <div class="control">
                  <input
                    id="clearingNumber"
                    v-model="formData.clearingNumber"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsFinancials.clearingNumber')
                    "
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="accountNumber">{{
                  t('adminSupplier.detailsFinancials.accountNumber')
                }}</label>
                <div class="control">
                  <input
                    id="accountNumber"
                    v-model="formData.accountNumber"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsFinancials.accountNumber')
                    "
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Accounting Section -->
          <div class="section-title">
            {{ t('adminSupplier.detailsAccounting.accounting') }}
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="defaultAccount">{{
                  t('adminSupplier.detailsAccounting.defaultAccount')
                }}</label>
                <div class="control">
                  <input
                    id="defaultAccount"
                    v-model="formData.defaultAccount"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsAccounting.defaultAccount')
                    "
                  />
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="vatNumber"
                  >{{ t('adminSupplier.detailsAccounting.vatNumber') }} *</label
                >
                <div class="control">
                  <input
                    id="vatNumber"
                    v-model="formData.vatNumber"
                    @input="isVatNumberManuallyEdited = true"
                    class="input"
                    type="text"
                    :placeholder="
                      t('adminSupplier.detailsAccounting.vatNumber')
                    "
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="vatType">{{
                  t('adminSupplier.detailsAccounting.standardVatType')
                }}</label>
                <div class="control">
                  <div class="select is-fullwidth">
                    <select id="vatType" v-model="formData.vatType">
                      <option value="">
                        {{ t('adminCreateInvoice.chooseVatType') }}
                      </option>
                      <option
                        v-for="type in vatTypeOptions"
                        :key="type.value"
                        :value="type.value"
                      >
                        {{ type.text }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="column">
              <div class="field">
                <label class="label" for="currency">{{
                  t('adminSupplier.detailsAccounting.currency')
                }}</label>
                <div class="control">
                  <input
                    id="currency"
                    class="input"
                    type="text"
                    value="SEK"
                    disabled
                    readonly
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer class="panel-footer">
          <button
            type="button"
            class="button"
            @click="handleClose"
            :disabled="isLoading"
          >
            {{ t('adminSupplier.button.cancel') }}
          </button>
          <button
            type="button"
            class="button is-primary has-text-weight-semibold"
            :class="{ 'is-loading': isLoading }"
            @click="handleSave"
            :disabled="
              isLoading ||
              !formData.organisationNumber.trim() ||
              !formData.name.trim() ||
              !formData.streetName.trim() ||
              !formData.buildingNumber.trim() ||
              !formData.zipCode.trim() ||
              !formData.city.trim() ||
              !formData.vatNumber.trim() ||
              (!formData.bankGiro.trim() &&
                !formData.postGiro.trim() &&
                (!formData.iban.trim() || !formData.bic.trim()))
            "
          >
            {{ t('adminSupplier.button.save') }}
          </button>
        </footer>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.slide-panel-container {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 1000;
  pointer-events: none;
}

.slide-panel {
  position: fixed;
  left: 1rem;
  bottom: 0;
  width: 50%;
  max-width: 800px;
  min-width: 600px;
  height: 90vh;
  background: white;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  border-radius: 12px;
  overflow: hidden;
}

// Vue transition classes for slide animation
.slide-enter-active {
  transition:
    transform 0.3s ease-out,
    opacity 0.3s ease-out;
}

.slide-leave-active {
  transition:
    transform 0.3s ease-in,
    opacity 0.3s ease-in;
}

.slide-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-enter-to {
  transform: translateY(0);
  opacity: 1;
}

.slide-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.panel-header {
  padding: 1.5rem;
  background: white;
  border-bottom: 1px solid #dbdbdb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;

  .delete {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    border-radius: 9999px;
  }
}

.panel-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.panel-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  color: #363636;

  &:first-child {
    margin-top: 0;
  }
}

.helper-text {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 1rem;
  font-style: italic;
}

.checkbox-label {
  margin-left: 0.5rem;
}

.panel-footer {
  padding: 1.5rem;
  background: white;
  border-top: 1px solid #dbdbdb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-shrink: 0;

  .button {
    border-radius: 9999px;
    padding: 0.5rem 1.5rem;

    &.is-loading::after {
      border-color: transparent transparent white white !important;
    }
  }
}
</style>
